import React, { useEffect, useMemo, useState } from 'react';
import Badge from '@amzn/awsui-components-console/badge';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import Header from '@amzn/awsui-components-console/header';
import Link from '@amzn/awsui-components-console/link';
import Pagination from '@amzn/awsui-components-console/pagination';
import Popover from '@amzn/awsui-components-console/popover';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Table, { TableProps } from '@amzn/awsui-components-console/table';
import Skeleton from '@amzn/awsui-components-console/skeleton';
import {
  DraftState,
  DraftStringInput,
  InlineMarkupType,
  MessageFormatType,
  StringInfo,
  TranslationStatus,
} from '../types';
import { MAX_STRINGS_PER_GROUP } from '../constants';
import { SUBMISSION_PENDING_LOCK_REASON_KEY } from '../constants';
import { sortItems } from 'src/common/utils/sorting';
import { useFollowNavigate } from 'src/common/hooks/useFollowNavigate';
import { buildStringPath } from 'src/common/routes';
import AccessDeniedAlert from 'src/common/authz/AccessDeniedAlert';
import { describeLoadFailure } from '../utils/snapshotError';
import {
  getAccessDeniedMessage,
  getAccessDeniedNamespace,
  isAccessDenied,
} from 'src/common/authz/isAccessDenied';
import styles from '../StringGroupDetail.module.scss';
import {
  pageSizeOf,
  TablePreferences,
  useTablePreferences,
} from 'src/common/ui/TablePreferences';
import {
  STRINGS_DEFAULT_PAGE_SIZE,
  buildStringsTablePreferences,
} from './stringsTablePreferences';
import { isLangHubClient } from '../utils/clientIdMapper';
import { useTranslation } from 'src/i18n/shim/useTranslation';

interface StringsTableProps {
  strings: StringInfo[];
  clientId: string;
  /** Namespace of the group — used to build the string detail links. */
  namespaceId: string;
  /** Group these strings belong to — used to build the string detail links. */
  stringGroupId: string;
  draftState: DraftState | null;
  /**
   * When false, editing controls (Create String, per-row Edit) are disabled
   * (grayed out) because the group is owned by a non-LangHub client.
   */
  canEdit?: boolean;

  /**
   * Whether the permission answer is still in flight. Gated controls show a skeleton rather
   * than a disabled state, so they never claim a missing permission before it is known.
   */
  permissionsLoading?: boolean;
  /**
   * When true, a submission is awaiting TRANSCOM confirmation, so Create String
   * is locked with a visible reason on hover — new strings can't be added to
   * the group until the in-flight submission is confirmed.
   */
  submissionPending?: boolean;
  /**
   * True while the initial strings snapshot is still loading. Strings now load
   * via a separate ListStrings query (not with the group metadata), so the
   * table owns its own loading state: it shows a spinner instead of an empty
   * body, and Create String is disabled so uniqueness validation never runs
   * against a snapshot that hasn't finished loading.
   */
  loading?: boolean;
  /**
   * Set when the strings snapshot failed to load. Rendered as a distinct error
   * state so an empty table is never mistaken for a load failure — the two are
   * indistinguishable otherwise now that strings load independently of the
   * group metadata (which has its own full-page error handling).
   */
  loadError?: { thrown: unknown } | null;
  onCreateClick?: () => void;
  onEditClick?: (stringInfo: StringInfo) => void;
}

/**
 * Represents either a parent string row or an expanded variant row in the
 * flattened table model.
 */
interface TableRow {
  /** Unique key for React rendering. */
  id: string;
  /** The parent StringInfo (always present). */
  stringInfo: StringInfo;
  /** Whether this row is a variant sub-row. */
  isVariant: boolean;
  /** For variant rows, the CX ID. */
  variantCxId?: string;
  /** For variant rows, the overridden source text. */
  variantSourceText?: string;
  /** For variant rows, the overridden context note. */
  variantContextNote?: string;
  /**
   * For variant rows, whether this exact cxId is present in the SERVER's
   * variant list for this string — the only case where the variant detail page
   * has something to show.
   *
   * `DescribeString` falls back to the base string when the requested cxId has
   * no override rather than 404ing, so linking a variant the server has never
   * seen produces a link that appears to work but renders the base string. The
   * parent's `draftStatus` cannot answer this: a variant added to an existing
   * (`edited`) string sits under a parent that is itself perfectly linkable.
   */
  isVariantLinkable?: boolean;
  /** Draft status for visual indicators. */
  draftStatus?: 'added' | 'edited' | 'deleted';
}

/** Converts a DraftStringInput to a StringInfo for table display. */
function draftToStringInfo(
  draft: DraftStringInput,
  clientId: string,
): StringInfo {
  return {
    stringId: draft.stringId,
    sourceLocale: draft.sourceLocale,
    sourceText: draft.sourceText,
    contextNote: draft.contextNote,
    inlineMarkupType: draft.inlineMarkupType ?? InlineMarkupType.PlainText,
    messageFormatType: draft.messageFormatType ?? MessageFormatType.None,
    clientId,
    variants: draft.variants ?? [],
    translationStatus: TranslationStatus.Complete,
  };
}

type MergedStringItem = {
  stringInfo: StringInfo;
  draftStatus?: 'added' | 'edited' | 'deleted';
};

/** Accessor used by `sortItems` to extract comparable values per field. */
export function sortAccessor(
  item: MergedStringItem,
  field: string,
): string | number | undefined {
  switch (field) {
    case 'stringId':
      return item.stringInfo.stringId;
    case 'sourceText':
      return item.stringInfo.sourceText;
    default:
      // If you add a new sortingField to columnDefinitions, add a case here.
      return undefined;
  }
}

/**
 * Wraps a cell's content in a bounded, independently scrollable box. Under
 * `resizableColumns` each column has a fixed, user-adjustable width, so a long
 * value would otherwise wrap and inflate the whole row's height. Bounding the
 * cell and letting it scroll keeps every column's overflow contained to that
 * column, scrolling on its own.
 */
const scrollableCell = (content: React.ReactNode): React.ReactNode => (
  <div className={styles.scrollableCell}>{content}</div>
);

const StringsTable: React.FC<StringsTableProps> = ({
  strings,
  clientId,
  namespaceId,
  stringGroupId,
  draftState,
  canEdit = true,
  permissionsLoading = false,
  submissionPending = false,
  loading = false,
  loadError = null,
  onCreateClick,
  onEditClick,
}) => {
  const { t } = useTranslation();
  // Keeps the string/variant detail links inside the SPA — a bare href here
  // reloads the document and re-runs AppBootstrap.
  const followNavigate = useFollowNavigate();

  const [currentPage, setCurrentPage] = useState(1);

  // Groups owned by a non-LangHub client have no group-level source locale
  // (DescribeStringGroup returns null — each string can differ), so the locale
  // is surfaced as a per-string column here. LangHub-owned groups share one
  // group-level locale shown in the metadata panel, so the column is omitted.
  const showSourceLocale = !isLangHubClient(clientId);
  const tablePreferencesConfig = useMemo(
    () =>
      buildStringsTablePreferences(t, {
        includeSourceLocale: showSourceLocale,
      }),
    // `t` is a dependency: the config resolves localized column labels, so a stale
    // `t` would persist headings from the previous locale.
    [t, showSourceLocale],
  );

  // Client-side paging. The clamp effect below only catches an out-of-range page;
  // a size change also has to return to page 1 or the user's rows move under them.
  const { preferences, onConfirm, columnDisplay } = useTablePreferences(
    tablePreferencesConfig,
    { onPageSizeChange: () => setCurrentPage(1) },
  );
  const pageSize = pageSizeOf(preferences, STRINGS_DEFAULT_PAGE_SIZE);
  const [expandedStringIds, setExpandedStringIds] = useState<Set<string>>(
    new Set(),
  );

  // Sorting state managed manually (useCollection not suitable for this table)
  const [sortingColumn, setSortingColumn] = useState<
    TableProps.SortingColumn<TableRow> | undefined
  >(undefined);
  const [sortingDescending, setSortingDescending] = useState(false);

  // Build the merged string list: server strings + draft additions
  const mergedStrings = useMemo(() => {
    const result: MergedStringItem[] = [];

    const deletedSet = new Set(draftState?.deleted ?? []);
    const editedMap = new Map(
      (draftState?.edited ?? []).map((e) => [e.stringId, e]),
    );
    const addedMap = new Map(
      (draftState?.added ?? []).map((a) => [a.stringId, a]),
    );

    // Track which stringIds from server data overlap with draft-added strings.
    // This prevents duplicates when the Apollo cache already contains strings
    // that were optimistically appended (e.g. via applyDraft) while the draft
    // hasn't been cleared yet.
    const seenServerStringIds = new Set<string>();

    // Server strings with draft annotations
    for (const str of strings) {
      seenServerStringIds.add(str.stringId);
      if (deletedSet.has(str.stringId)) {
        result.push({ stringInfo: str, draftStatus: 'deleted' });
      } else if (addedMap.has(str.stringId)) {
        // String already exists in server data AND is in draft.added — show it
        // as 'added' (draft wins) but don't append it again below.
        const draftAdd = addedMap.get(str.stringId)!;
        const mergedInfo: StringInfo = {
          ...str,
          sourceText: draftAdd.sourceText,
          contextNote: draftAdd.contextNote,
          inlineMarkupType: draftAdd.inlineMarkupType,
          messageFormatType: draftAdd.messageFormatType,
          variants: draftAdd.variants ?? str.variants,
        };
        result.push({ stringInfo: mergedInfo, draftStatus: 'added' });
      } else if (editedMap.has(str.stringId)) {
        // Merge draft-edited values into the display so the table shows updated text
        const draftEdit = editedMap.get(str.stringId)!;
        const mergedInfo: StringInfo = {
          ...str,
          sourceText: draftEdit.sourceText,
          contextNote: draftEdit.contextNote,
          inlineMarkupType: draftEdit.inlineMarkupType,
          messageFormatType: draftEdit.messageFormatType,
          variants: draftEdit.variants ?? str.variants,
        };
        result.push({ stringInfo: mergedInfo, draftStatus: 'edited' });
      } else {
        result.push({ stringInfo: str });
      }
    }

    // Append newly added strings from draft that are NOT already in server data
    for (const added of draftState?.added ?? []) {
      if (!seenServerStringIds.has(added.stringId)) {
        result.push({
          stringInfo: draftToStringInfo(added, clientId),
          draftStatus: 'added',
        });
      }
    }

    // Surface unsubmitted ("New") strings at the top so they're visible on the
    // first page instead of being buried after the published strings — they're
    // the rows the user is actively working on. Stable partition keeps the
    // relative order within each group.
    return [
      ...result.filter((item) => item.draftStatus === 'added'),
      ...result.filter((item) => item.draftStatus !== 'added'),
    ];
  }, [strings, draftState, clientId]);

  const effectiveTotalCount = mergedStrings.length;

  /**
   * Each string's variant CX IDs *as the server reports them*, keyed by stringId.
   *
   * Deliberately sourced from the unmerged `strings` prop rather than from
   * `mergedStrings`: the merge folds the draft's pending variants into
   * `stringInfo.variants`, so it can no longer distinguish a variant the server
   * knows from one that exists only in the local draft. A string absent from
   * this map has no server entry at all (draft-added), so none of its variants
   * are viewable.
   */
  const serverVariantCxIds = useMemo(() => {
    const byStringId = new Map<string, Set<string>>();
    for (const str of strings) {
      byStringId.set(str.stringId, new Set(str.variants.map((v) => v.cxId)));
    }
    return byStringId;
  }, [strings]);

  // Apply sorting to merged list. When the user explicitly sorts, all items
  // sort together. When no sort is active, the default ordering (added first)
  // is preserved from mergedStrings.
  const sortedStrings = useMemo(() => {
    if (!sortingColumn?.sortingField) return mergedStrings;
    return sortItems(
      mergedStrings,
      sortingColumn.sortingField,
      sortingDescending,
      sortAccessor,
    );
  }, [mergedStrings, sortingColumn, sortingDescending]);

  // Client-side pagination over sorted list
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedStrings.slice(start, start + pageSize);
  }, [sortedStrings, currentPage, pageSize]);

  // Variant sub-rows are inserted below their parent after sorting, so they
  // stay grouped regardless of sort order.
  const tableRows: TableRow[] = useMemo(() => {
    const rows: TableRow[] = [];
    for (const item of paginatedItems) {
      rows.push({
        id: item.stringInfo.stringId,
        stringInfo: item.stringInfo,
        isVariant: false,
        draftStatus: item.draftStatus,
      });
      if (expandedStringIds.has(item.stringInfo.stringId)) {
        // Resolved once per parent, then asked per variant — the answer differs
        // between the sub-rows of a single string (a draft-added variant sits
        // alongside the string's server variants).
        const serverCxIds = serverVariantCxIds.get(item.stringInfo.stringId);
        for (const variant of item.stringInfo.variants) {
          rows.push({
            id: `${item.stringInfo.stringId}__variant__${variant.cxId}`,
            stringInfo: item.stringInfo,
            isVariant: true,
            variantCxId: variant.cxId,
            variantSourceText: variant.sourceText,
            variantContextNote: variant.contextNote,
            isVariantLinkable: serverCxIds?.has(variant.cxId) ?? false,
            draftStatus: item.draftStatus,
          });
        }
      }
    }
    return rows;
  }, [paginatedItems, expandedStringIds, serverVariantCxIds]);

  const toggleExpand = (stringId: string) => {
    setExpandedStringIds((prev) => {
      const next = new Set(prev);
      if (next.has(stringId)) {
        next.delete(stringId);
      } else {
        next.add(stringId);
      }
      return next;
    });
  };

  const handleSortingChange = ({
    detail,
  }: {
    detail: TableProps.SortingState<TableRow>;
  }) => {
    setSortingColumn(detail.sortingColumn);
    setSortingDescending(detail.isDescending ?? false);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(effectiveTotalCount / pageSize);
  const rangeStart = (currentPage - 1) * pageSize + 1;
  const rangeEnd = Math.min(currentPage * pageSize, effectiveTotalCount);

  // The list can shrink underneath the current page (draft deletions, or
  // navigating to a smaller group), which would otherwise leave the user on an
  // out-of-range page rendering no rows. Guard on totalPages > 0 so an empty
  // list doesn't clamp to page 0.
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const renderStatusBadges = (row: TableRow) => {
    const badges: React.ReactNode[] = [];

    if (row.draftStatus === 'deleted') {
      // "Removed" (not "Deleted") is intentional: submitting the draft drops the
      // string from the group's next version rather than hard-deleting it, and a
      // true delete feature is still to come.
      badges.push(
        <Badge key="deleted" color="red">
          {t('sgd-badge-removed')}
        </Badge>,
      );
    } else if (row.draftStatus === 'added') {
      badges.push(
        <Badge key="new" color="blue">
          {t('sgd-badge-new')}
        </Badge>,
      );
    } else if (row.draftStatus === 'edited') {
      // A published string carrying unsaved draft edits. A published row with
      // no pending change gets no badge.
      badges.push(
        <Badge key="modified" color="grey">
          {t('sgd-badge-modified')}
        </Badge>,
      );
    }
    return (
      <SpaceBetween direction="horizontal" size="xxs">
        {badges}
      </SpaceBetween>
    );
  };

  /**
   * Link to a string's detail page, or to one of its variants.
   *
   * Mirrors `VariantsTable.buildVariantHref`'s `?variant=` shape so the two
   * entry points into the same page agree. No `?version=` is forwarded: this
   * page has no pinned-version concept — it always reads the group's latest.
   */
  const buildStringHref = (stringId: string, variantCxId?: string): string => {
    const path = buildStringPath(namespaceId, stringGroupId, stringId);
    if (variantCxId == null) {
      return path;
    }
    const query = new URLSearchParams();
    query.set('variant', variantCxId);
    return `${path}?${query.toString()}`;
  };

  const columnDefinitions: TableProps.ColumnDefinition<TableRow>[] = [
    {
      id: 'stringId',
      header: t('string-field-string-id'),
      sortingField: 'stringId',
      width: 260,
      minWidth: 180,
      cell: (row) => {
        if (row.isVariant) {
          // Plain link text, same as the parent row's stringId.
          // `cxId` is TRANSCOM taxonomy, so it is a parameter of one whole
          // label rather than a value concatenated onto a localized word.
          const label = t('sgd-variant-row-label', {
            cxId: row.variantCxId,
          });
          // Per-variant decision, precomputed on the row: only a variant the
          // server actually holds has a detail page worth linking to.
          return scrollableCell(
            <span className={styles.variantCellIndent}>
              {row.isVariantLinkable ? (
                <Link
                  href={buildStringHref(
                    row.stringInfo.stringId,
                    row.variantCxId,
                  )}
                  onFollow={followNavigate}
                  data-testid="variant-row-link"
                >
                  {label}
                </Link>
              ) : (
                <span data-testid="variant-row-plain">{label}</span>
              )}
            </span>,
          );
        }
        // A draft-added string does not exist on the server yet, so its detail
        // page would 404 — that row stays unlinked. A draft-DELETED string still
        // exists and is viewable, so it keeps its link.
        const isDraftAdded = row.draftStatus === 'added';
        const hasVariants = row.stringInfo.variants.length > 0;
        const isExpanded = expandedStringIds.has(row.stringInfo.stringId);
        const isDeleted = row.draftStatus === 'deleted';
        const label = isDeleted ? (
          <span className={styles.deletedText}>{row.stringInfo.stringId}</span>
        ) : (
          row.stringInfo.stringId
        );
        return (
          // The expand toggle is a sibling of the link, not its parent or child,
          // so neither swallows the other's click.
          scrollableCell(
            <SpaceBetween direction="horizontal" size="xxs">
              {hasVariants && (
                <button
                  className={styles.expandToggle}
                  onClick={() => toggleExpand(row.stringInfo.stringId)}
                  aria-label={t(
                    isExpanded
                      ? 'sgd-strings-collapse-variants-aria'
                      : 'sgd-strings-expand-variants-aria',
                    { stringId: row.stringInfo.stringId },
                  )}
                >
                  {isExpanded ? '▼' : '▶'}
                </button>
              )}
              {isDraftAdded ? (
                <span data-testid="string-id-plain">
                  {row.stringInfo.stringId}
                </span>
              ) : (
                <Link
                  href={buildStringHref(row.stringInfo.stringId)}
                  onFollow={followNavigate}
                  data-testid="string-id-link"
                >
                  {label}
                </Link>
              )}
              {renderStatusBadges(row)}
            </SpaceBetween>,
          )
        );
      },
    },
    ...(showSourceLocale
      ? [
          {
            id: 'sourceLocale',
            header: t('sgd-field-source-locale'),
            width: 160,
            minWidth: 120,
            // Variant sub-rows inherit their parent string's source locale, so
            // only the parent row displays it. `sourceLocale` is always present
            // on a server string, but guard for the em-dash so an empty value
            // never renders as a blank cell.
            cell: (row: TableRow) =>
              row.isVariant
                ? null
                : scrollableCell(row.stringInfo.sourceLocale || '—'),
          },
        ]
      : []),
    {
      id: 'sourceText',
      header: t('sgd-column-source-text'),
      sortingField: 'sourceText',
      width: 340,
      minWidth: 180,
      cell: (row) => {
        const text = row.isVariant
          ? row.variantSourceText
          : row.stringInfo.sourceText;
        const isDeleted = row.draftStatus === 'deleted';
        return scrollableCell(
          isDeleted ? <span className={styles.deletedText}>{text}</span> : text,
        );
      },
    },
    {
      id: 'contextNote',
      header: t('sgd-column-context-note'),
      width: 340,
      minWidth: 180,
      cell: (row) =>
        scrollableCell(
          row.isVariant
            ? (row.variantContextNote ?? '—')
            : (row.stringInfo.contextNote ?? '—'),
        ),
    },
    {
      id: 'actions',
      header: t('string-column-actions'),
      width: 140,
      minWidth: 140,
      cell: (row) => {
        if (row.draftStatus === 'deleted' || row.isVariant) {
          return null;
        }
        return (
          <SpaceBetween direction="horizontal" size="xxs">
            {permissionsLoading ? (
              <Skeleton display="inline-block" height="32px" width="60px" />
            ) : (
              canEdit && (
                <Button
                  disabled={!canEdit}
                  onClick={() => onEditClick?.(row.stringInfo)}
                >
                  {t('sgd-edit')}
                </Button>
              )
            )}
          </SpaceBetween>
        );
      },
    },
  ];

  // Reason text for the load-failure empty state: our own guard copy is already
  // localized, the service's own wording is passed through untranslated, and a
  // throwable with neither leaves the heading to speak for itself.
  const failure = loadError ? describeLoadFailure(t, loadError.thrown) : null;
  const failureReason = failure && failure.kind !== 'none' ? failure.text : '';

  return (
    <Table
      items={tableRows}
      columnDefinitions={columnDefinitions}
      columnDisplay={columnDisplay}
      preferences={
        <TablePreferences
          config={tablePreferencesConfig}
          preferences={preferences}
          onConfirm={onConfirm}
        />
      }
      variant="container"
      resizableColumns
      wrapLines={preferences.wrapLines}
      stripedRows={preferences.stripedRows}
      contentDensity={preferences.contentDensity}
      trackBy="id"
      ariaLabels={{ tableLabel: t('sgd-strings-table-aria') }}
      loading={loading}
      loadingText={t('sgd-strings-loading')}
      empty={
        loadError ? (
          /*
           * A refusal is not a load failure — the request was answered and
           * refused — so it shows the service's own wording rather than the
           * "Failed to load strings" framing. Rendered in the table's own empty
           * slot so the group header and metadata around it stay readable.
           */
          isAccessDenied(loadError.thrown) ? (
            <AccessDeniedAlert
              message={getAccessDeniedMessage(loadError.thrown)}
              deniedNamespace={getAccessDeniedNamespace(loadError.thrown)}
              data-testid="strings-access-denied"
            />
          ) : (
            <Box textAlign="center" color="inherit" padding="l">
              <b>{t('sgd-strings-error')}</b>
              {/* The reason goes UNDER the heading, which already says the load
                  failed — so a throwable with no reason of its own renders the
                  heading alone rather than repeating it as its own body. */}
              {failureReason && (
                <Box variant="p" color="inherit">
                  {failureReason}
                </Box>
              )}
            </Box>
          )
        ) : (
          <Box textAlign="center" color="inherit" padding="l">
            {t('sgd-strings-empty')}
          </Box>
        )
      }
      sortingColumn={sortingColumn}
      sortingDescending={sortingDescending}
      onSortingChange={handleSortingChange}
      header={
        <Header
          counter={`(${effectiveTotalCount})`}
          info={
            <span className={styles.paginationLabel}>
              {t('sgd-strings-table-max-hint', { max: MAX_STRINGS_PER_GROUP })}
            </span>
          }
          actions={
            permissionsLoading ? (
              <>
                <Skeleton display="inline-block" height="32px" width="120px" />
              </>
            ) : submissionPending && canEdit ? (
              // Locked by an in-flight submission (not a permission/ownership
              // gate), so surface the reason on hover.
              <Popover
                content={t(SUBMISSION_PENDING_LOCK_REASON_KEY)}
                dismissButton={false}
                position="top"
                size="medium"
                triggerType="custom"
              >
                <Button variant="primary" disabled>
                  {t('sgd-create-string')}
                </Button>
              </Popover>
            ) : (
              canEdit && (
                <Button
                  variant="primary"
                  disabled={!canEdit || submissionPending || loading}
                  onClick={onCreateClick}
                >
                  {t('sgd-create-string')}
                </Button>
              )
            )
          }
        >
          {t('sgd-strings-heading')}
        </Header>
      }
      pagination={
        <Pagination
          currentPageIndex={currentPage}
          pagesCount={totalPages}
          onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
          ariaLabels={{
            nextPageLabel: t('pagination-next'),
            previousPageLabel: t('pagination-previous'),
            pageLabel: (pageNumber) => t('pagination-page', { pageNumber }),
          }}
        />
      }
      footer={
        <span className={styles.paginationLabel}>
          {t('sgd-strings-table-range', {
            rangeStart,
            rangeEnd,
            total: effectiveTotalCount,
          })}
        </span>
      }
    />
  );
};

export default StringsTable;
